import React from 'react';
import {
  Avatar,
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { Plan, User } from '../../hooks/api/user';
import StudentPlanSelect from './StudentPlanSelect';

interface InstructorStudentsDesktopViewProps {
  students: User[];
  totalStudents: number;
  stats: Array<{ label: string; value: number; helper: string }>;
  plans: Plan[];
  searchText: string;
  showActiveOnly: boolean;
  page: number;
  rowsPerPage: number;
  onSearchChange: (searchText: string) => void;
  onToggleActiveOnly: () => void;
  onStudentClick: (userId: number) => void;
  onPlanChange: (event: SelectChangeEvent, userId: number) => void;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
}

export default function InstructorStudentsDesktopView({
  students,
  totalStudents,
  stats,
  plans,
  searchText,
  showActiveOnly,
  page,
  rowsPerPage,
  onSearchChange,
  onToggleActiveOnly,
  onStudentClick,
  onPlanChange,
  onPageChange,
  onRowsPerPageChange,
}: InstructorStudentsDesktopViewProps) {
  return (
    <Box sx={{ display: 'grid', gap: 3 }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 5,
          border: '1px solid rgba(18,18,18,0.08)',
          backgroundColor: 'white',
        }}
      >
        <Stack spacing={2.5}>
          <Stack
            direction={{ xs: 'column', xl: 'row' }}
            spacing={2}
            justifyContent="flex-end"
            alignItems={{ xs: 'stretch', xl: 'center' }}
          >
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <TextField
                className="student-searcher"
                label="Busca alumnos"
                variant="outlined"
                size="small"
                value={searchText}
                onChange={(event) => onSearchChange(event.target.value)}
                sx={{ minWidth: { xs: '100%', md: 260 } }}
              />
              <FormControlLabel
                control={<Checkbox checked={showActiveOnly} onChange={onToggleActiveOnly} />}
                label="Solo activos"
              />
            </Stack>
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(3, minmax(0, 1fr))',
              },
              gap: 1.5,
            }}
          >
            {stats.map((stat) => (
              <Paper
                key={stat.label}
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  border: '1px solid rgba(18,18,18,0.08)',
                  backgroundColor: '#f7f7f7',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {stat.label}
                </Typography>
                <Typography variant="h4" sx={{ my: 0.5 }}>
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stat.helper}
                </Typography>
              </Paper>
            ))}
          </Box>
        </Stack>
      </Paper>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 5,
          border: '1px solid rgba(18,18,18,0.08)',
          overflow: 'hidden',
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#111111' }}>
              <TableCell sx={{ color: 'white', borderBottom: 'none' }}>Alumno</TableCell>
              <TableCell sx={{ color: 'white', borderBottom: 'none' }}>Estado</TableCell>
              <TableCell sx={{ color: 'white', borderBottom: 'none' }}>Clases</TableCell>
              <TableCell sx={{ color: 'white', borderBottom: 'none' }}>Plan</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {students.map((user) => (
              <TableRow
                key={user.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => onStudentClick(user.id)}
              >
                <TableCell sx={{ borderBottomColor: 'rgba(18,18,18,0.08)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar alt={user.nickName} src={user.image} sx={{ width: 44, height: 44 }} />
                    <Box>
                      <Typography fontWeight={600}>
                        {user.nickName ?? `${user.firstName} ${user.lastName}`}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {`${user.firstName} ${user.lastName}`}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell sx={{ borderBottomColor: 'rgba(18,18,18,0.08)' }}>
                  <Chip
                    label={user.plan ? 'Activo' : 'Sin plan'}
                    size="small"
                    sx={{
                      bgcolor: user.plan ? '#eaeaea' : '#f7f7f7',
                      color: '#111111',
                      fontWeight: 600,
                    }}
                  />
                </TableCell>
                <TableCell sx={{ borderBottomColor: 'rgba(18,18,18,0.08)' }}>
                  <Typography fontWeight={600}>{user.planBookings}</Typography>
                </TableCell>
                <TableCell
                  sx={{ borderBottomColor: 'rgba(18,18,18,0.08)' }}
                  onClick={(event) => event.stopPropagation()}
                >
                  <StudentPlanSelect
                    planId={user.plan?.id}
                    plans={plans}
                    fullWidth
                    onChange={(event) => onPlanChange(event, user.id)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalStudents}
          page={page}
          onPageChange={(_, nextPage) => onPageChange(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => onRowsPerPageChange(Number(event.target.value))}
          rowsPerPageOptions={[25, 50, 100]}
          labelRowsPerPage="Filas por página"
        />
      </TableContainer>
    </Box>
  );
}
